/**
 * Phase 41.E/F + 42 + 43 + 45 — Truth Tab (Canonical 3-Panel View).
 *
 * Hard domain separation enforced:
 *   • Trading Engine Health   — stocks/portfolio/discovery/learning
 *   • AltMarkets Health       — odds/props/sports
 *   • System Global Integrity — SSE/refresh_authority/contamination
 *
 * Each panel binds to a single canonical /api/truth/* endpoint that
 * recomputes from raw live state — no cached UI metrics.
 *
 * UX language standardized: HEALTHY | DEGRADED | STALE | RECOVERING.
 */
import React, { useEffect, useState } from 'react';
import CornerPanel from '../components/CornerPanel';
import api from '../lib/api';
import { toast } from 'sonner';

// Canonical state → color class (Phase 45 §3).
const stateBorder = (s) =>
  s === 'HEALTHY'    ? 'border-buy'    :
  s === 'RECOVERING' ? 'border-cyan-sys' :
  s === 'STALE'      ? 'border-hold'   :
                        'border-sell';
const stateText = (s) =>
  s === 'HEALTHY'    ? 'text-buy'    :
  s === 'RECOVERING' ? 'text-cyan-sys' :
  s === 'STALE'      ? 'text-hold'   :
                        'text-sell';
const stateDot = (s) =>
  s === 'HEALTHY'    ? 'bg-buy'    :
  s === 'RECOVERING' ? 'bg-cyan-sys' :
  s === 'STALE'      ? 'bg-hold'   :
                        'bg-sell';

// ---- Metric label dictionary (Phase 45 §3 standardized labels) ------------
const TRADING_LABELS = {
  closed_trades:        'closed trades',
  win_rate_pct:         'win rate',
  profit_factor:        'profit factor',
  learning_band:        'learning band',
  recovery_score:       'recovery score',
  stale_quotes:         'stale quotes',
  discovery_age_s:      'discovery age',
  decision_cache_count: 'decision cache',
};
const ALT_LABELS = {
  authority_age_s:      'authority age',
  snapshot_version:     'snapshot ver',
  quota_state:          'quota state',
  quota_remaining:      'quota remain',
  prop_board_count:     'prop board',
  alt_refresh_state:    'refresh state',
  alt_refresh_age_s:    'refresh age',
  games_total:          'games total',
  games_live:           'games live',
};
const GLOBAL_LABELS = {
  sse_uptime_pct:             'SSE uptime',
  sse_last_emit_age_s:        'SSE last emit',
  sse_reconnects:             'SSE reconnects',
  refresh_authority_running:  'authority',
  cross_domain_contamination: 'contamination',
  truth_reliability_score:    'truth reliability',
};

// ---- Metric value formatters ---------------------------------------------
const fmtMetric = (key, value) => {
  if (value === null || value === undefined) return '—';
  if (key.endsWith('_pct') || key === 'sse_uptime_pct') {
    return `${value}%`;
  }
  if (key.endsWith('_age_s') || key === 'sse_last_emit_age_s') {
    if (typeof value === 'number') {
      if (value >= 3600) return `${Math.round(value / 3600)}h`;
      if (value >= 60)   return `${Math.round(value / 60)}m`;
      return `${value}s`;
    }
    return value;
  }
  if (key === 'profit_factor' && typeof value === 'number') {
    return value.toFixed(2);
  }
  if (typeof value === 'boolean') return value ? '✓ on' : '✗ off';
  return String(value);
};

// ---- Panel component -----------------------------------------------------
function TruthPanel({ title, data, labels, testIdPrefix }) {
  if (!data) {
    return (
      <CornerPanel title={title} testId={`${testIdPrefix}-loading`}>
        <div className="text-[var(--txt-muted)] font-mono text-[11px]">// loading...</div>
      </CornerPanel>
    );
  }
  const state = data.state || 'DEGRADED';
  const score = data.score ?? 0;
  const metrics = data.metrics || {};
  const issues = data.issues || [];

  return (
    <CornerPanel
      title={
        <div className="flex items-center gap-3">
          <span>{title}</span>
          <span className={`w-2 h-2 rounded-full ${stateDot(state)} animate-pulse`} />
          <span className={`text-[10px] uppercase tracking-[0.3em] ${stateText(state)}`}
                data-testid={`${testIdPrefix}-state`}>
            {state}
          </span>
        </div>
      }
      testId={`${testIdPrefix}-panel`}
    >
      <div className={`border-l-2 ${stateBorder(state)} pl-3`}>
        <div className="flex items-baseline gap-6 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--txt-muted)]">score</div>
            <div className={`font-mono text-[32px] tabular ${stateText(state)}`}
                 data-testid={`${testIdPrefix}-score`}>
              {score}
              <span className="text-[14px] text-[var(--txt-muted)]"> / 100</span>
            </div>
          </div>
          {issues.length > 0 && (
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--txt-muted)]">issues</div>
              <div className="flex flex-wrap gap-1 mt-1" data-testid={`${testIdPrefix}-issues`}>
                {issues.map((it, i) => (
                  <span key={i}
                        className="text-[10px] px-2 py-0.5 border border-hold text-hold">
                    {it}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-[11px]"
             data-testid={`${testIdPrefix}-metrics`}>
          {Object.entries(metrics).map(([key, val]) => (
            <div key={key} data-testid={`${testIdPrefix}-${key}`}>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--txt-muted)]">
                {(labels && labels[key]) || key.replaceAll('_', ' ')}
              </div>
              <div className="text-[14px] text-[var(--txt-primary)] mt-0.5">
                {fmtMetric(key, val)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </CornerPanel>
  );
}

// ---- Decision Intelligence panel (Phase 43 + 48.H) -----------------------
function DecisionIntelPanel({ trading }) {
  if (!trading) return null;
  const buildingDataset = (trading.sample_size || 0) < 5;
  const renderCalib = (curve) => {
    if (!curve || curve.length === 0) {
      return (
        <div className="text-[10px] text-cyan-sys"
             data-testid="decision-intel-building">
          // BUILDING DATASET — needs ≥5 resolved outcomes per bucket
        </div>
      );
    }
    return (
      <div className="space-y-1 font-mono text-[10px]">
        {curve.map((c, i) => {
          const delta = c.delta_pts;
          const ok = Math.abs(delta) < 10;
          return (
            <div key={i} className="grid grid-cols-[5rem_1fr_3rem_3rem] gap-2 items-center">
              <div className="text-[var(--txt-muted)]">{c.predicted_pct}%</div>
              <div className="h-2 bg-[var(--bg-darker)] rounded-sm overflow-hidden relative">
                <div className={`h-full ${ok ? 'bg-buy' : 'bg-sell'}`}
                     style={{ width: `${Math.min(100, c.actual_pct)}%` }} />
              </div>
              <div className="text-right">{c.actual_pct}%</div>
              <div className={`text-right ${ok ? 'text-buy' : 'text-sell'}`}>
                {delta > 0 ? '+' : ''}{delta}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const intelValue = (v, fmt) => {
    if (v == null || buildingDataset) return (
      <span className="text-cyan-sys text-[10px]">BUILDING</span>
    );
    return fmt ? fmt(v) : v;
  };

  return (
    <CornerPanel title="Decision Intelligence" testId="decision-intel-panel">
      <div data-testid="decision-intel-trading">
        <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys mb-2">
          trading · n={trading.sample_size} · {trading.lookback_days}d
        </div>
        {trading.overall && (
          <div className="grid grid-cols-3 gap-2 mb-3 font-mono text-[11px]">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">accuracy</div>
              <div className="text-[14px]" data-testid="decision-intel-accuracy">
                {intelValue(trading.overall.accuracy_pct, v => `${v}%`)}
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">avg roi</div>
              <div className="text-[14px]" data-testid="decision-intel-roi">
                {intelValue(trading.overall.avg_roi_usd, v => `$${v}`)}
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">expectancy</div>
              <div className="text-[14px]" data-testid="decision-intel-expectancy">
                {intelValue(trading.overall.expectancy_usd, v => `$${v}`)}
              </div>
            </div>
          </div>
        )}
        <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">calibration curve</div>
        {renderCalib(trading.calibration_curve)}
      </div>
    </CornerPanel>
  );
}

// ---- Drift Index Sparkline (Phase 47) ------------------------------------
function DriftSparkline({ points, drift_state, height = 24, width = 120 }) {
  if (!points || points.length < 2) {
    return <div className="text-[9px] text-[var(--txt-muted)]">// gathering data...</div>;
  }
  const scores = points.map((p) => p.score);
  const min = Math.min(...scores), max = Math.max(...scores);
  const range = Math.max(1, max - min);
  const stepX = width / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = i * stepX;
    const y = height - ((p.score - min) / range) * height;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const stroke =
    drift_state === 'IMPROVING_FAST' ? 'var(--buy)' :
    drift_state === 'IMPROVING'      ? 'var(--buy)' :
    drift_state === 'STABLE'         ? 'var(--cyan-sys)' :
    drift_state === 'DEGRADING'      ? 'var(--hold)' :
                                         'var(--sell)';
  return (
    <svg width={width} height={height} className="block">
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.2" />
    </svg>
  );
}

function DriftIndexPanel({ data }) {
  if (!data) return null;
  if ((data.sample_count || 0) < 2) {
    return (
      <CornerPanel title="Drift Index" testId="drift-index-panel">
        <div className="text-[10px] text-[var(--txt-muted)] font-mono">
          // gathering historical samples · {data.sample_count || 0} captured · refresh in 30s
        </div>
      </CornerPanel>
    );
  }
  const driftLabel = (s) =>
    s === 'IMPROVING_FAST' ? 'improving ↑↑' :
    s === 'IMPROVING'      ? 'improving ↑'  :
    s === 'STABLE'         ? 'stable ·'     :
    s === 'DEGRADING'      ? 'degrading ↓'  :
    s === 'DEGRADING_FAST' ? 'degrading ↓↓' :
                              s.toLowerCase();
  const driftColor = (s) =>
    s === 'IMPROVING_FAST' || s === 'IMPROVING' ? 'text-buy'    :
    s === 'STABLE'                                ? 'text-cyan-sys' :
    s === 'DEGRADING'                             ? 'text-hold'   :
                                                    'text-sell';
  const row = (label, p, testIdPrefix) => (
    <div className="grid grid-cols-[6rem_1fr_7rem_5rem_5rem] gap-3 items-center"
         data-testid={`drift-row-${testIdPrefix}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--txt-muted)]">{label}</div>
      <DriftSparkline points={p.points} drift_state={p.drift_state} />
      <div className={`text-[10px] tracking-wider ${driftColor(p.drift_state)}`}
           data-testid={`drift-state-${testIdPrefix}`}>
        {driftLabel(p.drift_state)}
      </div>
      <div className="text-right text-[11px] font-mono tabular">
        {p.current ?? '—'}/{p.mean ?? '—'}
      </div>
      <div className={`text-right text-[11px] font-mono tabular ${
        p.slope > 0 ? 'text-buy' : p.slope < 0 ? 'text-sell' : 'text-[var(--txt-muted)]'
      }`} data-testid={`drift-slope-${testIdPrefix}`}>
        {p.slope > 0 ? '+' : ''}{p.slope}
      </div>
    </div>
  );
  return (
    <CornerPanel
      title="Drift Index"
      testId="drift-index-panel"
      right={
        <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--txt-muted)]"
              data-testid="drift-sample-count">
          {data.window_hours}h · n={data.sample_count}
        </span>
      }
    >
      <div className="space-y-2 font-mono text-[11px]">
        <div className="grid grid-cols-[6rem_1fr_7rem_5rem_5rem] gap-3 text-[9px] uppercase tracking-wider text-[var(--txt-muted)] border-b border-[var(--border-default)] pb-1">
          <div>panel</div>
          <div>score trend</div>
          <div>state</div>
          <div className="text-right">now/mean</div>
          <div className="text-right">slope</div>
        </div>
        {row('Trading', data.trading, 'trading')}
        {row('AltMarkets', data.alt, 'alt')}
        {row('Global', data.global, 'global')}
      </div>
    </CornerPanel>
  );
}

// ---- Phase 47.A-F — Learning Recovery Center ----------------------------
const regimeStateColor = (s) =>
  s === 'ACTIVE'      ? 'text-buy'     :
  s === 'RECOVERING'  ? 'text-cyan-sys' :
  s === 'FROZEN'      ? 'text-hold'    :
  s === 'QUARANTINED' ? 'text-sell'    :
  s === 'DEGRADED'    ? 'text-sell'    :
                         'text-[var(--txt-muted)]';

const watchdogColor = (s) =>
  s === 'ACTIVE'   ? 'text-buy'     :
  s === 'WARNING'  ? 'text-hold'    :
  s === 'STALLED'  ? 'text-sell'    :
  s === 'CRITICAL' ? 'text-sell'    :
                      'text-[var(--txt-muted)]';

function _fmtAge(s) {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

function LearningRecoveryCenter({ audit, watchdog, integrity, calibration, releases, pipeline, recoveryV2, autoRestore, onRunRelease, onRebuildCalibration, onRunBootstrap }) {
  if (!audit) return null;
  return (
    <CornerPanel
      title={
        <div className="flex items-center gap-3">
          <span>Learning Recovery Center</span>
          <span className={`text-[10px] uppercase tracking-[0.3em] ${regimeStateColor(audit.state)}`}
                data-testid="lrc-state">
            {audit.state}
          </span>
        </div>
      }
      testId="learning-recovery-center"
    >
      <div className="space-y-4">
        {/* Top row — score / restrictions / watchdog */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">recovery score</div>
            <div className={`text-[20px] ${(recoveryV2?.score ?? audit.recovery_score ?? 0) >= 60 ? 'text-buy' : (recoveryV2?.score ?? audit.recovery_score ?? 0) >= 30 ? 'text-hold' : 'text-sell'}`}
                 data-testid="lrc-recovery-score">
              {recoveryV2?.score ?? audit.recovery_score ?? '—'}
              {recoveryV2?.band && (
                <span className="text-[9px] text-[var(--txt-muted)] ml-2">{recoveryV2.band}</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">restrictions</div>
            <div className="text-[11px]">
              <span className={audit.recovery_state.dampening_active ? 'text-hold' : 'text-[var(--txt-muted)]'}>
                ▪ dampen
              </span>{' · '}
              <span className={audit.recovery_state.safe_calibration_active ? 'text-sell' : 'text-[var(--txt-muted)]'}>
                ▪ safe-cal
              </span>
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">watchdog</div>
            <div className={`text-[14px] ${watchdogColor(watchdog?.state)}`}
                 data-testid="lrc-watchdog-state">{watchdog?.state || '—'}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">last outcome</div>
            <div className="text-[14px]" data-testid="lrc-last-outcome">
              {_fmtAge(audit.last_outcome_age_s)} ago
            </div>
          </div>
        </div>

        {/* Block reasons */}
        {audit.learning_block_reasons && Object.keys(audit.learning_block_reasons).length > 0 && (
          <div className="border-t border-[var(--border-default)] pt-3">
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">block reasons</div>
            <div className="flex flex-wrap gap-1" data-testid="lrc-block-reasons">
              {Object.entries(audit.learning_block_reasons).map(([reason, count]) => (
                <span key={reason}
                      className="text-[10px] px-2 py-0.5 border border-hold text-hold">
                  {reason.replaceAll('_', ' ')} × {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Phase 48.E — Recovery Score V2 breakdown */}
        {recoveryV2?.factors && (
          <div className="border-t border-[var(--border-default)] pt-3">
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">recovery score breakdown</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-[10px]"
                 data-testid="lrc-recovery-factors">
              {Object.entries(recoveryV2.factors).map(([k, v]) => {
                const max = 25;
                const pts = v.points ?? 0;
                const pct = (pts / max) * 100;
                return (
                  <div key={k} data-testid={`lrc-factor-${k}`}>
                    <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">{k}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[var(--bg-darker)] rounded-sm overflow-hidden">
                        <div className={`h-full ${pct >= 80 ? 'bg-buy' : pct >= 40 ? 'bg-hold' : 'bg-sell'}`}
                             style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px]">{pts}/{max}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Phase 48.A — Outcome Pipeline */}
        {pipeline && (
          <div className="border-t border-[var(--border-default)] pt-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">outcome pipeline · {pipeline.overall_integrity}</div>
              <button
                data-testid="lrc-run-bootstrap-btn"
                onClick={onRunBootstrap}
                className="text-[10px] px-2 py-0.5 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black uppercase tracking-wider"
              >run bootstrap</button>
            </div>
            {pipeline.bottleneck_stage && (
              <div className="text-[10px] text-sell mb-2" data-testid="lrc-pipeline-bottleneck">
                ⚠ bottleneck at <span className="font-bold">{pipeline.bottleneck_stage}</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 text-[10px] font-mono" data-testid="lrc-pipeline">
              {[['1h', pipeline.window_1h], ['24h', pipeline.window_24h], ['7d', pipeline.window_7d]].map(([label, w]) => (
                <div key={label}>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">{label} · {w.pipeline_integrity}</div>
                  <div className="text-[10px] mt-0.5">
                    <div>decisions: <span className="text-[var(--txt-primary)]">{w.decisions_actionable}</span></div>
                    <div>closed: <span className="text-[var(--txt-primary)]">{w.positions_closed}</span></div>
                    <div>outcomes: <span className="text-[var(--txt-primary)]">{w.outcomes_written}</span></div>
                    <div>learning: <span className="text-[var(--txt-primary)]">{w.outcomes_consumed_by_learning}</span></div>
                    <div>cal: <span className="text-[var(--txt-primary)]">{w.calibration_updates}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phase 48.F — Autonomous Restoration status */}
        {autoRestore && (
          <div className="border-t border-[var(--border-default)] pt-3"
               data-testid="lrc-auto-restore">
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">autonomous restoration</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-[10px]">
              <div>
                <div className="text-[9px] text-[var(--txt-muted)]">main loop</div>
                <div className={autoRestore.running ? 'text-buy' : 'text-sell'}>
                  {autoRestore.running ? '● running' : '○ stopped'} · {autoRestore.interval_s}s
                </div>
              </div>
              <div>
                <div className="text-[9px] text-[var(--txt-muted)]">restorations</div>
                <div>{autoRestore.restorations_total}</div>
              </div>
              <div>
                <div className="text-[9px] text-[var(--txt-muted)]">auto-restore watcher</div>
                <div className={autoRestore.watcher_running ? 'text-buy' : 'text-sell'}
                     data-testid="lrc-watcher-state">
                  {autoRestore.watcher_running ? '● running' : '○ stopped'} · +{autoRestore.growth_threshold_pct}%
                </div>
              </div>
              <div>
                <div className="text-[9px] text-[var(--txt-muted)]">watcher rebuilds</div>
                <div>{autoRestore.watcher_rebuilds_total}</div>
              </div>
            </div>
            {autoRestore.last_action && (
              <div className="text-[9px] text-[var(--txt-muted)] mt-1">
                last: {autoRestore.last_action.score_before} → {autoRestore.last_action.score_after}
                {autoRestore.last_action.released > 0 && <> · released {autoRestore.last_action.released}</>}
                {autoRestore.last_action.outcomes_added > 0 && <> · +{autoRestore.last_action.outcomes_added} outcomes</>}
              </div>
            )}
          </div>
        )}

        {/* Per-regime breakdown — Phase 47.F */}
        <div className="border-t border-[var(--border-default)] pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">regime detail</div>
            <button
              data-testid="lrc-run-release-btn"
              onClick={onRunRelease}
              className="text-[10px] px-2 py-0.5 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black uppercase tracking-wider"
            >run release pass</button>
          </div>
          <div className="font-mono text-[10px] space-y-1.5" data-testid="lrc-regimes">
            <div className="grid grid-cols-[8rem_1fr_5rem_5rem_5rem_6rem] gap-2 text-[9px] uppercase tracking-wider text-[var(--txt-muted)] pb-1 border-b border-[var(--border-default)]">
              <div>regime</div>
              <div>samples / required</div>
              <div className="text-right">win rate</div>
              <div className="text-right">drift</div>
              <div className="text-right">ETA</div>
              <div className="text-right">state</div>
            </div>
            {(audit.regimes || []).map((r) => {
              const pct = Math.min(100, (r.samples / r.required_samples) * 100);
              return (
                <div key={r.regime} className="grid grid-cols-[8rem_1fr_5rem_5rem_5rem_6rem] gap-2 items-center"
                     data-testid={`lrc-regime-${r.regime}`}>
                  <div className="text-[var(--txt-primary)]">{r.regime}</div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[var(--bg-darker)] rounded-sm overflow-hidden">
                        <div className={`h-full ${r.samples >= r.required_samples ? 'bg-buy' : 'bg-hold'}`}
                             style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[9px] text-[var(--txt-muted)]">{r.samples}/{r.required_samples}</span>
                    </div>
                    {r.freeze_reason && (
                      <div className="text-[8px] text-sell mt-0.5">
                        {r.freeze_reason.replaceAll('_', ' ')}
                      </div>
                    )}
                  </div>
                  <div className="text-right">{r.win_rate_pct == null ? '—' : `${r.win_rate_pct}%`}</div>
                  <div className={`text-right ${
                    r.confidence_drift_pts == null ? 'text-[var(--txt-muted)]' :
                    Math.abs(r.confidence_drift_pts) < 15 ? 'text-buy' : 'text-sell'
                  }`}>
                    {r.confidence_drift_pts == null ? '—' : `${r.confidence_drift_pts}pt`}
                  </div>
                  <div className="text-right text-[var(--txt-muted)]">{r.recovery_eta}</div>
                  <div className={`text-right ${regimeStateColor(r.state)}`}>{r.state}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Decision integrity + calibration + releases */}
        <div className="border-t border-[var(--border-default)] pt-3 grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-[11px]">
          {integrity && (
            <div data-testid="lrc-decision-integrity">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">decision integrity</div>
              <div className="text-[20px] tabular">{integrity.integrity_pct}%</div>
              <div className="text-[9px] text-[var(--txt-muted)]">
                created {integrity.created} · resolved {integrity.resolved} · pending {integrity.pending}
                {integrity.orphaned > 0 && <> · <span className="text-sell">orphaned {integrity.orphaned}</span></>}
              </div>
            </div>
          )}
          {calibration && (
            <div data-testid="lrc-calibration">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">calibration rebuild</div>
                <button
                  data-testid="lrc-rebuild-cal-btn"
                  onClick={onRebuildCalibration}
                  className="text-[9px] px-1.5 py-0 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black"
                >rebuild</button>
              </div>
              <div className="text-[20px] tabular">
                {calibration.latest?.calibration_health ?? '—'}
                <span className="text-[10px] text-[var(--txt-muted)]"> health</span>
              </div>
              <div className="text-[9px] text-[var(--txt-muted)]">
                {calibration.latest?.samples_used ?? 0} samples · {calibration.latest?.buckets?.length ?? 0} buckets
              </div>
            </div>
          )}
          {releases && (
            <div data-testid="lrc-releases">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">regime releases</div>
              <div className="text-[20px] tabular">{releases.releases_total ?? 0}</div>
              <div className="text-[9px] text-[var(--txt-muted)]">
                {releases.last_release
                  ? <>last: {releases.last_release.regime} · {new Date((releases.last_release.ts || 0) * 1000).toLocaleTimeString()}</>
                  : <>no releases yet · next pass in ≤5m</>}
              </div>
            </div>
          )}
        </div>

        {/* Watchdog detail */}
        {watchdog && (
          <div className="border-t border-[var(--border-default)] pt-3">
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-1">learning event ages</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-[10px]" data-testid="lrc-watchdog-items">
              {Object.entries(watchdog.items || {}).map(([k, v]) => (
                <div key={k}>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">
                    {k.replaceAll('_', ' ').replace('last ', '')}
                  </div>
                  <div className={`text-[12px] ${watchdogColor(v.state)}`} data-testid={`lrc-wd-${k}`}>
                    {_fmtAge(v.age_s)}
                    <span className="text-[9px] text-[var(--txt-muted)]"> · {v.state}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CornerPanel>
  );
}

// ---- Phase 49 — Regime Truth Repair panel --------------------------------
function RegimeTruthPanel({ regimeAudit, attribution, integrityCheck, failureGuard, quarantineDebug, reconcile, onApplyReconcile, onEvaluateCorrection, correctionMode, learningState }) {
  if (!regimeAudit && !attribution && !integrityCheck) return null;
  const integrityClass = (s) =>
    s >= 80 ? 'text-buy' : s >= 50 ? 'text-hold' : 'text-sell';
  return (
    <CornerPanel
      title={
        <div className="flex items-center gap-3">
          <span>Regime Truth Repair</span>
          {integrityCheck && (
            <span className={`text-[10px] uppercase tracking-[0.3em] ${integrityClass(integrityCheck.integrity_score)}`}
                  data-testid="rtr-integrity-state">
              integrity {integrityCheck.integrity_score}%
            </span>
          )}
        </div>
      }
      testId="regime-truth-repair"
    >
      <div className="space-y-4">
        {/* Per-regime recompute */}
        {regimeAudit?.regimes && (
          <div data-testid="rtr-regimes">
            <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)] mb-2">recomputed regime truth</div>
            <div className="font-mono text-[10px] space-y-1">
              <div className="grid grid-cols-[7rem_4rem_4rem_5rem_5rem_5rem_1fr] gap-2 text-[9px] uppercase tracking-wider text-[var(--txt-muted)] pb-1 border-b border-[var(--border-default)]">
                <div>regime</div>
                <div className="text-right">total</div>
                <div className="text-right">W/L</div>
                <div className="text-right">true wr</div>
                <div className="text-right">dev</div>
                <div className="text-right">cached</div>
                <div>attribution</div>
              </div>
              {regimeAudit.regimes.map((r) => {
                const cached = (reconcile?.regimes || []).find((x) => x.regime === r.regime);
                const diff = cached?.difference_pts;
                return (
                  <div key={r.regime} className="grid grid-cols-[7rem_4rem_4rem_5rem_5rem_5rem_1fr] gap-2 items-center"
                       data-testid={`rtr-regime-${r.regime}`}>
                    <div>{r.regime}</div>
                    <div className="text-right">{r.total_trades}</div>
                    <div className="text-right">{r.wins}/{r.losses}</div>
                    <div className={`text-right ${(r.win_rate_pct ?? 0) >= 45 ? 'text-buy' : 'text-sell'}`}>
                      {r.win_rate_pct == null ? '—' : `${r.win_rate_pct}%`}
                    </div>
                    <div className={`text-right ${(r.deviation_pts ?? 0) < 15 ? 'text-buy' : 'text-sell'}`}>
                      {r.deviation_pts == null ? '—' : `${r.deviation_pts}pt`}
                    </div>
                    <div className="text-right text-[var(--txt-muted)]">
                      {cached?.cached_win_rate == null ? '—' : `${cached.cached_win_rate}%`}
                      {diff != null && Math.abs(diff) > 5 && (
                        <span className={`ml-1 text-[8px] ${diff > 0 ? 'text-buy' : 'text-sell'}`}>
                          {diff > 0 ? '+' : ''}{diff}
                        </span>
                      )}
                    </div>
                    <div className="text-[9px]">
                      {r.attribution_issues?.length ? (
                        <span className="text-sell">{r.attribution_issues.join(', ')}</span>
                      ) : <span className="text-buy">ok</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Attribution + Integrity grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-[var(--border-default)] pt-3">
          {attribution && (
            <div data-testid="rtr-attribution">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">attribution validity</div>
              <div className={`text-[18px] tabular ${integrityClass(attribution.integrity_pct)}`}>
                {attribution.integrity_pct}%
              </div>
              <div className="text-[9px] text-[var(--txt-muted)]">
                {attribution.valid_outcomes}/{attribution.total_outcomes} valid
              </div>
              {Object.entries(attribution.issue_counts || {}).map(([k, v]) => (
                v > 0 && <div key={k} className="text-[9px] text-sell">{k}: {v}</div>
              ))}
            </div>
          )}
          {integrityCheck && (
            <div data-testid="rtr-integrity">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">integrity cross-check</div>
              <div className={`text-[18px] tabular ${integrityClass(integrityCheck.integrity_score)}`}>
                {integrityCheck.integrity_score}%
              </div>
              <div className="text-[9px] text-[var(--txt-muted)]">
                {integrityCheck.total_outcomes} outcomes · {integrityCheck.issue_total} issues
              </div>
              {Object.entries(integrityCheck.issues || {}).map(([k, v]) => (
                v > 0 && <div key={k} className="text-[9px] text-sell">{k}: {v}</div>
              ))}
            </div>
          )}
          {failureGuard && (
            <div data-testid="rtr-failure-guard">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">failure guard</div>
              <div className={`text-[14px] ${failureGuard.guard_state === 'ACTIVE' ? 'text-sell' : 'text-buy'}`}>
                {failureGuard.guard_state}
              </div>
              <div className="text-[9px] text-[var(--txt-muted)]">
                {failureGuard.trigger_reason || 'no trigger'}
              </div>
              <div className="text-[9px]">
                streak: <span className="text-sell">{failureGuard.streak_losses}L</span> · regimes: {failureGuard.regimes_affected?.join(', ') || '—'}
              </div>
              {/* Fix F — resolver-truth overlay (Phase 50 failure-guard-v2 risk score) */}
              {learningState && (
                <div className="mt-1 pt-1 border-t border-[var(--border-subtle,#1a1a1a)] text-[9px]"
                     data-testid="rtr-failure-guard-v2-overlay">
                  v2 risk: <span className="text-hold">
                    {Math.round(learningState.execution_alignment?.target_alignment?.value ?? 0)}
                  </span> · coherence: <span className="text-cyan-sys">
                    {learningState.system_coherence_score}/100
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quarantine debug */}
        {quarantineDebug?.regimes?.length > 0 && (
          <div className="border-t border-[var(--border-default)] pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">quarantine debugger</div>
              {/* Fix F — resolver-truth badge: clarify that Phase 2 weighted mode
                  means these labels are HISTORICAL detections, not active gates. */}
              {learningState?.quarantine_state && (
                <div className={`text-[9px] uppercase tracking-[0.25em] px-2 py-0.5 border ${
                  learningState.quarantine_state.active
                    ? 'border-sell text-sell'
                    : 'border-buy text-buy'
                }`}
                  data-testid="rtr-quarantine-resolver-status"
                  title={`mode=${learningState.quarantine_state.mode} · min_data=${learningState.quarantine_state.min_data_mode}`}>
                  {learningState.quarantine_state.active
                    ? 'BLOCKING'
                    : `${learningState.quarantine_state.mode || 'weighted'} — historical only`}
                </div>
              )}
            </div>
            <div className="space-y-1.5" data-testid="rtr-quarantine">
              {quarantineDebug.regimes.map((q) => (
                <div key={q.regime} className="font-mono text-[10px] grid grid-cols-[7rem_5rem_1fr_6rem] gap-2 items-center"
                     data-testid={`rtr-quarantine-${q.regime}`}>
                  <div>{q.regime}</div>
                  <div className={q.state === 'ACTIVE' ? 'text-buy' : (learningState?.quarantine_state?.active === false ? 'text-hold' : 'text-sell')}>{q.state}</div>
                  <div className="text-[9px]">{q.reasons.join(' · ') || 'no reasons'}</div>
                  <div className={`text-right text-[9px] ${q.fully_releasable ? 'text-buy' : 'text-hold'}`}>
                    {q.fully_releasable ? 'releasable' : 'blocked'}
                  </div>
                </div>
              ))}
              {/* Weight badges from Phase 2 resolver — visible truth of suppression strength */}
              {learningState?.quarantine_state?.severity_weights &&
               Object.keys(learningState.quarantine_state.severity_weights).length > 0 && (
                <div className="mt-2 pt-2 border-t border-[var(--border-subtle,#1a1a1a)] text-[9px] text-[var(--txt-muted)]"
                     data-testid="rtr-quarantine-weights">
                  phase-2 weighted suppression — actual influence multipliers:
                  {Object.entries(learningState.quarantine_state.severity_weights).map(([r, w]) => (
                    <span key={r} className="ml-2 text-cyan-sys">{r}={w}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action buttons + correction mode status */}
        <div className="border-t border-[var(--border-default)] pt-3 flex flex-wrap items-center gap-2">
          <button data-testid="rtr-reconcile-btn"
                  onClick={onApplyReconcile}
                  className="text-[10px] px-2 py-1 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black uppercase tracking-wider">
            apply reconciliation
          </button>
          <button data-testid="rtr-correction-btn"
                  onClick={onEvaluateCorrection}
                  className="text-[10px] px-2 py-1 border border-sell text-sell hover:bg-sell hover:text-black uppercase tracking-wider">
            evaluate correction mode
          </button>
          {correctionMode && (
            <span className={`text-[10px] ${correctionMode.active ? 'text-sell' : 'text-[var(--txt-muted)]'}`}
                  data-testid="rtr-correction-state">
              correction mode: {correctionMode.active ? '● ACTIVE' : '○ inactive'}
            </span>
          )}
        </div>
      </div>
    </CornerPanel>
  );
}

// ---- Main Truth page -----------------------------------------------------
// ---- Phase 50.L — Strategy Forensics panel ---------------------------
function ForensicCard({ label, value, sub, tone, testId }) {
  const toneClass =
    tone === 'good'    ? 'text-buy'    :
    tone === 'warn'    ? 'text-hold'   :
    tone === 'bad'     ? 'text-sell'   :
    tone === 'neutral' ? 'text-cyan-sys' :
                         'text-[var(--txt-primary)]';
  return (
    <div className="panel p-3" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-[0.25em] text-[var(--txt-muted)]">{label}</div>
      <div className={`font-mono text-[18px] mt-1 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function StrategyForensicsPanel({ summary }) {
  if (!summary) {
    return (
      <CornerPanel title="Strategy Forensics" testId="strategy-forensics-panel">
        <div className="text-[10px] text-cyan-sys" data-testid="strategy-forensics-loading">
          // loading forensics ...
        </div>
      </CornerPanel>
    );
  }
  const et  = summary.entry_timing      || {};
  const sl  = summary.stop_loss         || {};
  const tq  = summary.target_quality    || {};
  const rc  = summary.regime_classifier || {};
  const de  = summary.discovery_execution || {};
  const sp  = summary.strategy_performance || {};
  const fg  = summary.failure_guard     || {};

  const lateTone = (et.late_rate_pct ?? 0) < 20 ? 'good' :
                    (et.late_rate_pct ?? 0) < 60 ? 'warn' : 'bad';
  const targetTone = (tq.target_quality_score ?? 0) >= 60 ? 'good' :
                      (tq.target_quality_score ?? 0) >= 30 ? 'warn' : 'bad';
  const regimeTone = rc.state === 'ACCURATE' ? 'good' :
                      rc.state === 'NOISY' ? 'warn' : 'bad';
  const driftTone = de.drift_state === 'HEALTHY' ? 'good' :
                     de.drift_state === 'WATCH' ? 'warn' :
                     de.drift_state === 'DEGRADED' ? 'warn' : 'bad';
  const stratTone = sp.state === 'HEALTHY' ? 'good' :
                     sp.state === 'WATCH' ? 'neutral' :
                     sp.state === 'DEGRADED' ? 'warn' : 'bad';
  const guardTone = fg.mode === 'NORMAL' ? 'good' :
                     fg.mode === 'CAUTION' ? 'warn' :
                     fg.mode === 'DEFENSIVE' ? 'warn' : 'bad';
  const sefTone = (sl.stop_efficiency_pct ?? 0) >= 70 ? 'good' :
                   (sl.stop_efficiency_pct ?? 0) >= 40 ? 'warn' : 'bad';

  return (
    <CornerPanel title="Strategy Forensics" testId="strategy-forensics-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys mb-3">
        one-screen diagnosis · /api/forensics/summary
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <ForensicCard
          label="Strategy Health"
          value={sp.strategy_health_score ?? '—'}
          sub={sp.state || '—'}
          tone={stratTone}
          testId="forensics-strategy-health"
        />
        <ForensicCard
          label="Failure Guard"
          value={fg.mode || 'NORMAL'}
          sub={`risk ${fg.risk_score ?? 0}`}
          tone={guardTone}
          testId="forensics-failure-guard"
        />
        <ForensicCard
          label="Regime Accuracy"
          value={`${rc.regime_accuracy_pct ?? 0}%`}
          sub={rc.state || '—'}
          tone={regimeTone}
          testId="forensics-regime"
        />
        <ForensicCard
          label="Entry Timing"
          value={`${et.late_rate_pct ?? 0}%`}
          sub={`avg ${Math.round(et.avg_delay_s ?? 0)}s late`}
          tone={lateTone}
          testId="forensics-entry-timing"
        />
        <ForensicCard
          label="Stop Efficiency"
          value={`${sl.stop_efficiency_pct ?? '—'}%`}
          sub={`${sl.stop_failures ?? 0} fires · ${sl.target_hits ?? 0} targets`}
          tone={sefTone}
          testId="forensics-stop"
        />
        <ForensicCard
          label="Target Quality"
          value={tq.target_quality_score ?? 0}
          sub={`hit ${tq.target_hit_rate_pct ?? 0}%`}
          tone={targetTone}
          testId="forensics-target"
        />
        <ForensicCard
          label="Discovery↔Exec"
          value={de.grade_consistency_score ?? 0}
          sub={de.drift_state || '—'}
          tone={driftTone}
          testId="forensics-discovery"
        />
      </div>
      {fg.reasons && fg.reasons.length > 0 && (
        <div className="mt-3 text-[10px] font-mono text-[var(--txt-secondary)]"
             data-testid="forensics-failure-reasons">
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">
            failure-guard reasons
          </div>
          {fg.reasons.map((r, i) => (
            <div key={i} className="text-cyan-sys">// {r}</div>
          ))}
        </div>
      )}
    </CornerPanel>
  );
}

// ---------------------------------------------------------------------------
// Phase 1 (post-desync) — System Coherence panel.
// Surfaces the single-source-of-truth resolver: coherence score, global
// state, learning_allowed, contradictions list. Read-only.
// ---------------------------------------------------------------------------
function SystemCoherencePanel({ resolver }) {
  if (!resolver) {
    return (
      <CornerPanel title="System Coherence" testId="system-coherence-panel">
        <div className="text-[10px] text-cyan-sys" data-testid="system-coherence-loading">
          // resolving system state ...
        </div>
      </CornerPanel>
    );
  }
  const coherence  = resolver.system_coherence_score ?? 0;
  const conflicts  = resolver.system_conflicts || [];
  const global     = resolver.global_state || 'UNKNOWN';
  const allowed    = !!resolver.learning_allowed;
  const quarantine = resolver.quarantine_state || {};
  const cohTone    = coherence >= 80 ? 'good' : coherence >= 60 ? 'warn' : 'bad';
  const stateTone  = global === 'HEALTHY' ? 'good' :
                     global === 'RECOVERING' ? 'warn' :
                     global === 'DEGRADED' ? 'warn' : 'bad';

  return (
    <CornerPanel
      title="System Coherence"
      testId="system-coherence-panel"
      right={
        <span data-testid="system-coherence-allowed"
              className={`text-[10px] uppercase tracking-[0.3em] px-2 py-1 border ${
                allowed ? 'border-buy text-buy' : 'border-sell text-sell'
              }`}>
          learning {allowed ? 'allowed' : 'blocked'}
        </span>
      }
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys mb-3">
        Phase 1 · /api/system/learning-state · single source of truth
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <ForensicCard
          label="Coherence"
          value={`${coherence}/100`}
          sub={`${conflicts.length} conflicts`}
          tone={cohTone}
          testId="coherence-score"
        />
        <ForensicCard
          label="Global State"
          value={global}
          sub={`recover ${resolver.learning_health?.recovery_score?.value ?? 0}`}
          tone={stateTone}
          testId="coherence-global-state"
        />
        <ForensicCard
          label="Regime Stability"
          value={`${Math.round(resolver.regime_health?.stability_score?.value ?? 0)}`}
          sub={`acc ${Math.round(resolver.regime_health?.accuracy?.value ?? 0)}%`}
          tone={(resolver.regime_health?.stability_score?.value ?? 0) >= 60 ? 'good' :
                 (resolver.regime_health?.stability_score?.value ?? 0) >= 30 ? 'warn' : 'bad'}
          testId="coherence-regime-stability"
        />
        <ForensicCard
          label="Quarantine"
          value={quarantine.active ? 'ACTIVE' : 'CLEAR'}
          sub={`${(quarantine.reasons || []).length} reasons`}
          tone={quarantine.active ? 'bad' : 'good'}
          testId="coherence-quarantine"
        />
      </div>
      {conflicts.length > 0 && (
        <div className="text-[10px] font-mono text-[var(--txt-secondary)]"
             data-testid="coherence-conflicts-list">
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">
            detected contradictions
          </div>
          {conflicts.map((c, i) => (
            <div key={i} className="text-hold" data-testid={`coherence-conflict-${i}`}>
              // {c.replace(/_/g, ' ')}
            </div>
          ))}
        </div>
      )}
      {conflicts.length === 0 && (
        <div className="text-[10px] font-mono text-[var(--txt-muted)]"
             data-testid="coherence-conflicts-empty">
          // no cross-subsystem contradictions detected
        </div>
      )}
    </CornerPanel>
  );
}


// ---------------------------------------------------------------------------
// Phase 50.M — Execution Diagnostics Panel (READ-ONLY)
// Phase 1 (post-desync): hydrate primarily from resolver.panel_overrides.execution
// so we never display "-" when the underlying engine has data. Falls back to
// the per-engine endpoints when the resolver hasn't loaded yet.
// ---------------------------------------------------------------------------
function ExecutionDiagnosticsPanel({ latency, bottlenecks, stale, resolver }) {
  const ov = resolver?.panel_overrides?.execution;
  if (!ov && !latency && !bottlenecks && !stale) {
    return (
      <CornerPanel title="Execution Diagnostics" testId="execution-diagnostics-panel">
        <div className="text-[10px] text-cyan-sys" data-testid="execution-diagnostics-loading">
          // loading execution diagnostics ...
        </div>
      </CornerPanel>
    );
  }
  // Resolver (truth) → fallback to raw engine reads.
  const avgMs    = ov?.avg_latency_ms ?? null;
  const p95Ms    = ov?.p95_latency_ms ?? null;
  const maxMs    = ov?.max_latency_ms ?? null;
  const samples  = ov?.samples ?? latency?.positions_joined ?? 0;
  const coverage = ov?.coverage_pct ?? latency?.coverage_pct ?? 0;
  const quality  = ov?.quality_score ?? '—';
  const latTone  = quality === 'GOOD'     ? 'good' :
                   quality === 'WATCH'    ? 'warn' :
                   quality === 'DEGRADED' ? 'warn' :
                   quality === 'CRITICAL' ? 'bad'  : 'neutral';
  const staleN   = ov?.stale_count ?? stale?.rejections_24h ?? 0;
  const staleMode = ov?.stale_guard_mode ?? (ov?.stale_guard_enabled
                       ? (ov?.stale_guard_dry_run ? 'DRY_RUN' : 'ACTIVE')
                       : 'DISABLED');
  const staleTone = staleMode === 'DISABLED' ? 'neutral'
                     : staleMode === 'DRY_RUN' ? 'warn'
                     : staleN === 0 ? 'good' : staleN < 5 ? 'warn' : 'bad';
  const bottleN  = ov?.bottleneck_count ?? bottlenecks?.cause_count ?? 0;
  const bottleState = ov?.bottleneck_state ?? bottlenecks?.state ?? '—';
  const bottleTone = bottleState === 'HEALTHY' ? 'good' :
                     bottleState === 'WATCH'   ? 'warn' :
                     bottleState === '—'       ? 'neutral' : 'bad';

  const fmtMs = (v) => v == null ? '—' : v >= 1000 ? `${(v/1000).toFixed(1)}s` : `${Math.round(v)}ms`;

  return (
    <CornerPanel title="Execution Diagnostics" testId="execution-diagnostics-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys mb-3">
        Phase 50.M · read-only · {resolver ? 'resolver-hydrated' : '/api/execution/*'}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        <ForensicCard
          label="Avg Latency"
          value={fmtMs(avgMs)}
          sub={`${samples} samples · ${Math.round(coverage)}% cov`}
          tone={latTone}
          testId="exec-avg-latency"
        />
        <ForensicCard
          label="P95 Latency"
          value={fmtMs(p95Ms)}
          sub={maxMs != null ? `median ${fmtMs(maxMs)}` : '—'}
          tone={latTone}
          testId="exec-p95-latency"
        />
        <ForensicCard
          label="Bottlenecks"
          value={bottleN}
          sub={bottleState}
          tone={bottleTone}
          testId="exec-bottlenecks"
        />
        <ForensicCard
          label="Stale Signals"
          value={staleMode === 'DISABLED' ? 'OFF' : staleN}
          sub={staleMode === 'DISABLED'
                 ? 'guard disabled · telemetry only'
                 : staleMode === 'DRY_RUN'
                   ? `DRY-RUN · >${ov?.stale_threshold_s || 0}s · 24h`
                   : ov?.stale_threshold_s
                     ? `>${ov.stale_threshold_s}s · 24h`
                     : '24h window'}
          tone={staleTone}
          testId="exec-stale-signals"
        />
        <ForensicCard
          label="Execution Quality"
          value={quality}
          sub={latency?.bottleneck_stage || (coverage < 50 ? 'low coverage' : '—')}
          tone={latTone}
          testId="exec-quality"
        />
      </div>
      {bottleN > 0 && bottlenecks?.causes && (
        <div className="mt-3 text-[10px] font-mono text-[var(--txt-secondary)]"
             data-testid="exec-bottlenecks-list">
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">
            detected causes
          </div>
          {bottlenecks.causes.slice(0, 5).map((c, i) => (
            <div key={i} className="text-cyan-sys">
              // {typeof c === 'string' ? c : (c.cause || c.name || JSON.stringify(c))}
            </div>
          ))}
        </div>
      )}
    </CornerPanel>
  );
}

// ---------------------------------------------------------------------------
// Phase 50.N — Regime Confusion Matrix Panel (READ-ONLY)
// Phase 1: hydrate F1/unknown/high-conf from resolver.panel_overrides.regime
// (the per-engine endpoint paths used different key names, causing "-").
// ---------------------------------------------------------------------------
function RegimeConfusionMatrixPanel({ validator, unknown, confidence, resolver }) {
  if (!validator) {
    return (
      <CornerPanel title="Regime Classification Truth" testId="regime-confusion-panel">
        <div className="text-[10px] text-cyan-sys" data-testid="regime-confusion-loading">
          // loading regime validator ...
        </div>
      </CornerPanel>
    );
  }
  const ov         = resolver?.panel_overrides?.regime;
  const acc        = ov?.accuracy_pct ?? validator.accuracy_pct ?? 0;
  const f1         = ov?.f1_score ?? (validator.macros && validator.macros.macro_f1) ?? null;
  const matrix     = validator.matrix || validator.confusion_matrix || {};
  const confusions = validator.biggest_confusions || [];
  const labels     = Array.isArray(validator.labels) && validator.labels.length
                       ? validator.labels
                       : Object.keys(matrix);
  const accTone    = acc >= 70 ? 'good' : acc >= 50 ? 'warn' : 'bad';
  const unkPct     = ov?.unknown_share_pct ?? unknown?.unknown_rate_pct ?? null;
  const dominant   = ov?.dominant_reason ?? null;
  const highPct    = ov?.high_conf_pct ?? null;
  const lowPct     = ov?.low_conf_pct ?? null;

  return (
    <CornerPanel title="Regime Classification Truth" testId="regime-confusion-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys mb-3">
        Phase 50.N · read-only · {resolver ? 'resolver-hydrated' : '/api/regime/*'}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <ForensicCard
          label="Accuracy"
          value={`${Math.round(acc)}%`}
          sub={`${validator.n ?? 0} labels`}
          tone={accTone}
          testId="regime-accuracy"
        />
        <ForensicCard
          label="F1 Score"
          value={f1 != null ? Number(f1).toFixed(2) : '—'}
          sub={validator.headline || '—'}
          tone={accTone}
          testId="regime-f1"
        />
        <ForensicCard
          label="Unknown Share"
          value={unkPct != null ? `${Math.round(unkPct)}%` : '—'}
          sub={dominant || unknown?.by_category && Object.keys(unknown.by_category)[0] || '—'}
          tone={unkPct == null ? 'neutral' : unkPct < 15 ? 'good' : unkPct < 35 ? 'warn' : 'bad'}
          testId="regime-unknown"
        />
        <ForensicCard
          label="High-Conf %"
          value={highPct != null ? `${Math.round(highPct)}%` : '—'}
          sub={lowPct != null ? `low ${Math.round(lowPct)}%` : '—'}
          tone={highPct == null ? 'neutral' : highPct >= 50 ? 'good' : highPct >= 25 ? 'warn' : 'bad'}
          testId="regime-confidence-dist"
        />
      </div>
      {labels.length > 0 && (
        <div className="overflow-x-auto" data-testid="regime-matrix-table">
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">
            confusion matrix · rows = predicted · cols = truth
          </div>
          <table className="text-[10px] font-mono w-full border-collapse">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left text-[var(--txt-muted)]">pred ↓ / truth →</th>
                {labels.map((c) => (
                  <th key={c} className="px-2 py-1 text-right text-[var(--txt-muted)]">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((r) => (
                <tr key={r} className="border-t border-[var(--border-subtle,#1a1a1a)]">
                  <td className="px-2 py-1 text-[var(--txt-primary)]">{r}</td>
                  {labels.map((c) => {
                    const v = matrix[r]?.[c] ?? 0;
                    const isDiag = r === c;
                    return (
                      <td key={c}
                          className={`px-2 py-1 text-right tabular ${
                            isDiag && v > 0 ? 'text-buy' :
                            !isDiag && v > 0 ? 'text-hold' :
                            'text-[var(--txt-muted)]'
                          }`}>
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confusions.length > 0 && (
        <div className="mt-3 text-[10px] font-mono text-[var(--txt-secondary)]"
             data-testid="regime-confusion-list">
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">
            biggest confusions
          </div>
          {confusions.slice(0, 5).map((c, i) => (
            <div key={i} className="text-cyan-sys">
              // predicted {c.predicted || c.from || '?'} → truth {c.truth || c.to || '?'} · {c.count || c.n || 0}×
            </div>
          ))}
        </div>
      )}
    </CornerPanel>
  );
}

// ---------------------------------------------------------------------------
// Phase 50.O — Adaptive Target Engine Panel (READ-ONLY)
// ---------------------------------------------------------------------------
function AdaptiveTargetEnginePanel({ optimizer, balance, mfeMae, recs }) {
  if (!optimizer && !balance && !mfeMae && !recs) {
    return (
      <CornerPanel title="Adaptive Target Engine" testId="target-engine-panel">
        <div className="text-[10px] text-cyan-sys" data-testid="target-engine-loading">
          // loading target engine ...
        </div>
      </CornerPanel>
    );
  }
  const hit    = balance?.target_hit_rate_pct;
  const stopHit = balance?.stop_hit_rate_pct;
  const realism = balance?.reward_realism_score;
  const eff    = balance?.target_efficiency_pct;
  const realismTone = realism == null ? 'neutral' :
                      realism >= 80 ? 'good' :
                      realism >= 50 ? 'warn' : 'bad';
  const hitTone  = hit == null ? 'neutral' :
                   hit >= 25 ? 'good' :
                   hit >= 15 ? 'warn' : 'bad';
  const stopTone = stopHit == null ? 'neutral' :
                   stopHit <= 25 ? 'good' :
                   stopHit <= 40 ? 'warn' : 'bad';
  const recRows  = recs?.by_regime || [];
  const diags    = mfeMae || {};
  const diagBad  = (diags.targets_too_far || 0) + (diags.stops_too_tight || 0) + (diags.exits_too_early || 0);
  const diagTone = diagBad === 0 ? 'good' : diagBad < 5 ? 'warn' : 'bad';

  return (
    <CornerPanel title="Adaptive Target Engine" testId="target-engine-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys mb-3">
        Phase 50.O · read-only · /api/targets/*
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-3">
        <ForensicCard
          label="Reward Realism"
          value={realism != null ? `${realism}` : '—'}
          sub={`spec ${balance?.spec_target_hit_pct ?? 30}% hit`}
          tone={realismTone}
          testId="target-realism"
        />
        <ForensicCard
          label="Target Hit Rate"
          value={hit != null ? `${hit}%` : '—'}
          sub={`n=${balance?.n ?? 0}`}
          tone={hitTone}
          testId="target-hit-rate"
        />
        <ForensicCard
          label="Stop Hit Rate"
          value={stopHit != null ? `${stopHit}%` : '—'}
          sub={eff != null ? `eff ${eff}%` : '—'}
          tone={stopTone}
          testId="target-stop-rate"
        />
        <ForensicCard
          label="Global Optimal"
          value={optimizer?.overall_recommended_target_pct != null
                  ? `${optimizer.overall_recommended_target_pct}%`
                  : '—'}
          sub={`${optimizer?.overall_n_winners ?? 0} winners`}
          tone="neutral"
          testId="target-global-optimal"
        />
        <ForensicCard
          label="MFE/MAE Diag"
          value={diagBad}
          sub={`cov ${diags.coverage_pct ?? 0}%`}
          tone={diagTone}
          testId="target-diag-count"
        />
      </div>

      {(diags.targets_too_far || diags.stops_too_tight || diags.exits_too_early) ? (
        <div className="grid grid-cols-3 gap-2 mb-3 text-[10px] font-mono"
             data-testid="target-diag-breakdown">
          <div className="panel p-2">
            <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">targets too far</div>
            <div className="text-hold text-[14px] mt-1">{diags.targets_too_far ?? 0}</div>
          </div>
          <div className="panel p-2">
            <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">stops too tight</div>
            <div className="text-hold text-[14px] mt-1">{diags.stops_too_tight ?? 0}</div>
          </div>
          <div className="panel p-2">
            <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">exits too early</div>
            <div className="text-hold text-[14px] mt-1">{diags.exits_too_early ?? 0}</div>
          </div>
        </div>
      ) : null}

      {recRows.length > 0 ? (
        <div className="overflow-x-auto" data-testid="target-recs-table">
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">
            per-regime recommendations (read-only)
          </div>
          <table className="text-[10px] font-mono w-full border-collapse">
            <thead>
              <tr className="text-[var(--txt-muted)]">
                <th className="px-2 py-1 text-left">regime</th>
                <th className="px-2 py-1 text-right">target%</th>
                <th className="px-2 py-1 text-right">stop%</th>
                <th className="px-2 py-1 text-right">R:R</th>
                <th className="px-2 py-1 text-right">n</th>
                <th className="px-2 py-1 text-right">conf</th>
                <th className="px-2 py-1 text-left">reason</th>
              </tr>
            </thead>
            <tbody>
              {recRows.map((r, i) => (
                <tr key={i}
                    className="border-t border-[var(--border-subtle,#1a1a1a)]"
                    data-testid={`target-rec-row-${i}`}>
                  <td className="px-2 py-1 text-[var(--txt-primary)]">{r.regime}</td>
                  <td className="px-2 py-1 text-right text-buy">{r.recommended_target_pct ?? '—'}</td>
                  <td className="px-2 py-1 text-right text-sell">{r.recommended_stop_pct ?? '—'}</td>
                  <td className="px-2 py-1 text-right text-cyan-sys">{r.recommended_rr ?? '—'}</td>
                  <td className="px-2 py-1 text-right">{r.n}</td>
                  <td className="px-2 py-1 text-right">{r.confidence}</td>
                  <td className="px-2 py-1 text-[var(--txt-muted)]">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-[10px] font-mono text-[var(--txt-muted)]"
             data-testid="target-recs-empty">
          // no per-regime recommendations yet — awaiting more winning samples
        </div>
      )}
    </CornerPanel>
  );
}

export default function Truth() {
  const [snapshot, setSnapshot] = useState(null);
  const [contamination, setContamination] = useState(null);
  const [decisionsT, setDecisionsT] = useState(null);
  const [drift, setDrift] = useState(null);
  const [audit, setAudit] = useState(null);
  const [watchdog, setWatchdog] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [releases, setReleases] = useState(null);
  // Phase 48.A-F additions
  const [pipeline, setPipeline] = useState(null);
  const [recoveryV2, setRecoveryV2] = useState(null);
  const [autoRestore, setAutoRestore] = useState(null);
  // Phase 49 additions
  const [regimeAudit, setRegimeAudit] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [integrityCheck, setIntegrityCheck] = useState(null);
  const [failureGuard, setFailureGuard] = useState(null);
  const [quarantineDebug, setQuarantineDebug] = useState(null);
  const [reconcile, setReconcile] = useState(null);
  const [correctionMode, setCorrectionMode] = useState(null);
  const [forensicsSummary, setForensicsSummary] = useState(null);   // 50.L
  // Phase 50.M — Execution diagnostics
  const [execLatency, setExecLatency]       = useState(null);
  const [execBottlenecks, setExecBottlenecks] = useState(null);
  const [execStale, setExecStale]           = useState(null);
  // Phase 50.N — Regime classification
  const [regimeValidator, setRegimeValidator] = useState(null);
  const [regimeUnknown, setRegimeUnknown]   = useState(null);
  const [regimeConfidence, setRegimeConfidence] = useState(null);
  // Phase 50.O — Adaptive Target Engine
  const [tgtOptimizer, setTgtOptimizer]     = useState(null);
  const [tgtBalance, setTgtBalance]         = useState(null);
  const [tgtMfeMae, setTgtMfeMae]           = useState(null);
  const [tgtRecs, setTgtRecs]               = useState(null);
  // Phase 1 post-desync — Single Source of Truth resolver
  const [learningState, setLearningState]   = useState(null);
  const [err, setErr] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/truth/snapshot');
      setSnapshot(data);
      setErr(false);
    } catch { setErr(true); }
    try {
      const { data } = await api.get('/truth/contamination');
      setContamination(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/truth/decisions/trading');
      setDecisionsT(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/truth/drift?window_hours=24');
      setDrift(data);
    } catch { /* non-fatal */ }
    // Phase 47.A-F — Learning Recovery Center
    try {
      const { data } = await api.get('/learning/recovery-audit');
      setAudit(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/event-watchdog');
      setWatchdog(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/decision-integrity');
      setIntegrity(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/calibration-rebuild');
      setCalibration(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/regime-release/status');
      setReleases(data);
    } catch { /* non-fatal */ }
    // Phase 48.A — Outcome pipeline audit
    try {
      const { data } = await api.get('/learning/outcome-pipeline-audit');
      setPipeline(data);
    } catch { /* non-fatal */ }
    // Phase 48.E — Recovery Score V2 (breakdown)
    try {
      const { data } = await api.get('/learning/recovery-score-v2');
      setRecoveryV2(data);
    } catch { /* non-fatal */ }
    // Phase 48.F — Autonomous Restoration status
    try {
      const { data } = await api.get('/learning/autonomous-restoration/status');
      setAutoRestore(data);
    } catch { /* non-fatal */ }
    // Phase 49 — Regime Truth Repair diagnostics
    try {
      const { data } = await api.get('/learning/regime-outcome-audit');
      setRegimeAudit(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/attribution-validate');
      setAttribution(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/integrity-check');
      setIntegrityCheck(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/failure-guard-explain');
      setFailureGuard(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/quarantine-debug');
      setQuarantineDebug(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/regime-reconcile');
      setReconcile(data);
    } catch { /* non-fatal */ }
    try {
      const { data } = await api.get('/learning/correction-mode/status');
      setCorrectionMode(data);
    } catch { /* non-fatal */ }
    // Phase 50.L — Strategy Forensics aggregate (one-call summary)
    try {
      const { data } = await api.get('/forensics/summary');
      setForensicsSummary(data);
    } catch { /* non-fatal */ }
    // Phase 50.M — Execution diagnostics (read-only)
    try { const { data } = await api.get('/execution/latency');     setExecLatency(data); }     catch { /* non-fatal */ }
    try { const { data } = await api.get('/execution/bottlenecks'); setExecBottlenecks(data); } catch { /* non-fatal */ }
    try { const { data } = await api.get('/execution/stale-signals'); setExecStale(data); }    catch { /* non-fatal */ }
    // Phase 50.N — Regime classification (read-only)
    try { const { data } = await api.get('/regime/validator');         setRegimeValidator(data); }  catch { /* non-fatal */ }
    try { const { data } = await api.get('/regime/unknown-analysis');  setRegimeUnknown(data); }    catch { /* non-fatal */ }
    try { const { data } = await api.get('/regime/confidence');        setRegimeConfidence(data); } catch { /* non-fatal */ }
    // Phase 50.O — Adaptive Target Engine (read-only)
    try { const { data } = await api.get('/targets/optimizer');       setTgtOptimizer(data); } catch { /* non-fatal */ }
    try { const { data } = await api.get('/targets/balance');         setTgtBalance(data); }   catch { /* non-fatal */ }
    try { const { data } = await api.get('/targets/mfe-mae');         setTgtMfeMae(data); }    catch { /* non-fatal */ }
    try { const { data } = await api.get('/targets/recommendations'); setTgtRecs(data); }      catch { /* non-fatal */ }
    // Phase 1 post-desync — Single Source of Truth resolver
    try { const { data } = await api.get('/system/learning-state');   setLearningState(data); } catch { /* non-fatal */ }
  };

  const applyReconcile = async () => {
    try {
      const { data } = await api.post('/learning/regime-reconcile/apply');
      const drift = data?.max_drift_pts ?? 0;
      toast.success(`reconciliation applied · max drift ${drift}pt`);
      load();
    } catch { toast.error('reconcile failed'); }
  };

  const evaluateCorrection = async () => {
    try {
      const { data } = await api.post('/learning/correction-mode/evaluate');
      if (data?.activated_now) {
        toast.success(`correction mode ACTIVATED · cleared ${data.cleared_quarantines.length} quarantines`);
      } else {
        toast.info(`correction not activated · integrity=${data.integrity_score}% attr=${data.attribution_pct}%`);
      }
      load();
    } catch { toast.error('correction evaluation failed'); }
  };

  const runBootstrap = async () => {
    try {
      const { data } = await api.post('/learning/bootstrap');
      const added = data?.backfill?.inserted ?? 0;
      const released = data?.release?.released_count ?? 0;
      toast.success(`bootstrap · +${added} outcomes · released ${released}`);
      load();
    } catch { toast.error('bootstrap failed'); }
  };

  const runRegimeRelease = async () => {
    try {
      const { data } = await api.post('/learning/regime-release/run');
      if (data?.released_count > 0) toast.success(`released ${data.released_count} regime(s)`);
      else toast.info('no regimes met release thresholds');
      load();
    } catch { toast.error('release pass failed'); }
  };

  const rebuildCalibration = async () => {
    try {
      const { data } = await api.post('/learning/calibration-rebuild/run');
      toast.success(`calibration rebuilt · ${data?.samples_used ?? 0} samples`);
      load();
    } catch { toast.error('rebuild failed'); }
  };

  useEffect(() => {
    // Defer the first load to the next tick so the setState calls
    // inside `load` don't fire synchronously during the effect commit
    // (satisfies react-hooks/set-state-in-effect lint rule).
    const t = setTimeout(load, 0);
    const iv = setInterval(load, 30000);
    return () => { clearTimeout(t); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err && !snapshot) {
    return (
      <div data-testid="truth-page-error" className="p-4 font-mono text-[var(--txt-muted)]">
        // truth surface unreachable — retrying...
      </div>
    );
  }

  const overall = snapshot?.truth_state || 'DEGRADED';

  return (
    <div className="p-2 md:p-4 space-y-3" data-testid="truth-page">
      {/* Overall Truth State header */}
      <CornerPanel
        title="System Truth"
        testId="truth-overall-panel"
        right={
          contamination && (
            <span
              data-testid="truth-contam-pill"
              className={`text-[10px] uppercase tracking-[0.3em] px-2 py-1 border ${
                contamination.cross_contamination
                  ? 'border-sell text-sell'
                  : 'border-buy text-buy'
              }`}
            >
              contamination {contamination.cross_contamination_count}
            </span>
          )
        }
      >
        <div className="flex items-center gap-6">
          <span className={`w-4 h-4 rounded-full ${stateDot(overall)} animate-pulse`} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--txt-muted)]">truth state</div>
            <div className={`font-display text-[24px] ${stateText(overall)}`}
                 data-testid="truth-overall-state">
              {overall}
            </div>
          </div>
          {contamination && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--txt-muted)]">reliability</div>
              <div className="font-mono text-[20px] tabular"
                   data-testid="truth-reliability">
                {contamination.truth_reliability_score}%
              </div>
            </div>
          )}
        </div>
      </CornerPanel>

      {/* Phase 1 (post-desync) — System Coherence (single source of truth) */}
      <SystemCoherencePanel resolver={learningState} />

      {/* Phase 48 — Trading-only Truth Tab (4 canonical panels).
          AltMarkets domain removed from product. */}
      <TruthPanel
        title="Trading Engine"
        data={snapshot?.trading}
        labels={TRADING_LABELS}
        testIdPrefix="trading"
      />
      <TruthPanel
        title="Platform Health"
        data={snapshot?.global}
        labels={GLOBAL_LABELS}
        testIdPrefix="global"
      />

      {/* Phase 50.L — Strategy Forensics (one-screen diagnosis) */}
      <StrategyForensicsPanel summary={forensicsSummary} />

      {/* Phase 50.M — Execution Diagnostics (READ-ONLY) */}
      <ExecutionDiagnosticsPanel
        latency={execLatency}
        bottlenecks={execBottlenecks}
        stale={execStale}
        resolver={learningState}
      />

      {/* Phase 50.N — Regime Confusion Matrix (READ-ONLY) */}
      <RegimeConfusionMatrixPanel
        validator={regimeValidator}
        unknown={regimeUnknown}
        confidence={regimeConfidence}
        resolver={learningState}
      />

      {/* Phase 50.O — Adaptive Target Engine (READ-ONLY) */}
      <AdaptiveTargetEnginePanel
        optimizer={tgtOptimizer}
        balance={tgtBalance}
        mfeMae={tgtMfeMae}
        recs={tgtRecs}
      />

      {/* Decision Intelligence — Phase 43 + Phase 48.H BUILDING DATASET */}
      <DecisionIntelPanel trading={decisionsT} />

      {/* Drift Index — Phase 47 trend layer (trading + global only) */}
      <DriftIndexPanel data={drift} />

      {/* Phase 47.A-F + 48.A-F — Learning Recovery Center
          (Outcome Pipeline + Recovery Score V2 + Autonomous Restoration) */}
      <LearningRecoveryCenter
        audit={audit}
        watchdog={watchdog}
        integrity={integrity}
        calibration={calibration}
        releases={releases}
        pipeline={pipeline}
        recoveryV2={recoveryV2}
        autoRestore={autoRestore}
        onRunRelease={runRegimeRelease}
        onRebuildCalibration={rebuildCalibration}
        onRunBootstrap={runBootstrap}
      />

      {/* Phase 49 — Regime Truth Repair + Attribution Integrity */}
      <RegimeTruthPanel
        regimeAudit={regimeAudit}
        attribution={attribution}
        integrityCheck={integrityCheck}
        failureGuard={failureGuard}
        quarantineDebug={quarantineDebug}
        reconcile={reconcile}
        correctionMode={correctionMode}
        onApplyReconcile={applyReconcile}
        onEvaluateCorrection={evaluateCorrection}
        learningState={learningState}
      />
    </div>
  );
}
